import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Drawer,
  TextField,
  Grid,
  IconButton,
  FormHelperText,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import DrawerToolbar from 'common/components/drawer/drawer-toolbar';
import { CustomerTagTitle } from 'components/setting/models/customer-tag-title';
import { useFormik } from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomerSettingsApi from 'components/setting/api/customer-settings-api';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import AppSnackbar from 'components/snackbar/snackbar';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: '400px',
    animation: `$slideIn 300ms ${theme.transitions.easing.easeInOut}`,
  },
  '@keyframes slideIn': {
    '0%': {
      transform: 'translateX(100%)',
    },
    '100%': {
      transform: 'translateX(0)',
    },
  },
  titleCount: { marginTop: '4px', textAlign: 'right' },
}));

const TagTitleLimit = 90 as const;
const TagLimit = 90 as const;
function CustomerTagDrawer({
  tagProps,
  open,
  onClose,
  fetchTags,
}: {
  tagProps?: any;
  open: boolean;
  onClose: any;
  fetchTags: any;
}) {
  const classes = useStyles();
  const [showSnackbar] = AppSnackbar.useSnackBar();
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [formValuesChanged, setFormValuesChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tagError, setTagError] = useState<boolean>(false);

  const initialValues: CustomerTagTitle = {
    id: tagProps?.id || null,
    dealerId: tagProps?.dealerId || null,
    title: tagProps?.title || '',
    customerTagOptions: tagProps?.customerTagOptions || [],
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => {
      setFormValuesChanged(true);
      const errors: Partial<any> = {};
      if (!values.title) {
         errors.title = 'Tag Category is required';
      }
      let tagErr: boolean = false;
      values.customerTagOptions.forEach((tagOption: any) => {
        if (tagOption.tag.length == 0) {
          tagErr = true;
        }
      });
      setTagError(tagErr);
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        values.customerTagOptions = values.customerTagOptions.filter((to: any) => !(to.isDeleted && to.isNew));
        if (tagProps == null) {
          await CustomerSettingsApi.insertCustomerTagSetting(values);
        } else {
          await CustomerSettingsApi.updateCustomerTags(values);
        }
        onClose(false);
        fetchTags();
      } catch (error) {
        console.error('Error saving customer tags:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleAddTagOption = () => {
    setAddingTag(true);
  };

  const handleNewTagChange = (e: any) => {
    setNewTag(e.target.value);
  };

  const handleNewTagSubmit = () => {
    if (newTag.trim()) {
      formik.setFieldValue('customerTagOptions', [...formik.values.customerTagOptions, { tag: newTag, isNew: true }]);
      setNewTag('');
      setAddingTag(false);
    }
  };

  const handleDeleteTagOption = (item: any, index: number) => {
    if (item.alreadyAssigned) {
      showSnackbar({ type: 'error', message: 'Tag is already assigned to a customer. You cannot delete it.' });
      return;
    }

    if (index != -1) formik.setFieldValue(`customerTagOptions[${index}].isDeleted`, true);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onClose(false);
      }}
      PaperProps={{ className: classes.drawerPaper }}>
      <DrawerToolbar
        title="Add Customer Tags"
        onClose={() => {
          onClose(false);
        }}
      />
      <Box style={{ padding: '16px' }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                value={formik.values.title}
                name={'title'}
                required
                autoFocus
                size="small"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Tag Category"
                variant="outlined"
                fullWidth
                label="Tag Category"
                inputProps={{
                  maxLength: TagTitleLimit,
                }}
                error={formik.touched.title && Boolean(formik.errors.title)}
              />
              <div className={classes.titleCount}>
                <Typography
                  variant="caption"
                  color={formik.values.title.length === TagTitleLimit ? 'error' : 'textSecondary'}>
                  {formik.values.title.length}/{TagTitleLimit}
                </Typography>
              </div>

              {formik.touched.title && formik.errors.title && (
                <FormHelperText error>{formik.errors.title}</FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              {formik.values.customerTagOptions?.map((tagOption: any, index: any) => (
                <Grid
                  container
                  spacing={1}
                  alignItems="center"
                  key={index}
                  style={{ display: tagOption.isDeleted ? 'none' : '' }}>
                  <Grid item xs={10}>
                    <TextField
                      name={`customerTagOptions[${index}].tag`}
                      value={formik.values.customerTagOptions[index].tag}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder={`Tag ${index + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      label={`Tag ${index + 1}`}
                      inputProps={{
                        maxLength: TagLimit,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              variant="caption"
                              color={
                                formik.values.customerTagOptions[index].tag.length == TagLimit
                                  ? 'error'
                                  : 'textSecondary'
                              }>
                              {formik.values.customerTagOptions[index].tag.length}/{TagLimit}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      error={formik.values.customerTagOptions[index].tag.length == 0}
                      helperText={formik.values.customerTagOptions[index].tag.length == 0 && 'Tag required'}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton onClick={() => handleDeleteTagOption(tagOption, index)} aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            {addingTag && (
              <Grid item xs={12}>
                <TextField
                  value={newTag}
                  onChange={handleNewTagChange}
                  placeholder="New Tag Option"
                  variant="outlined"
                  fullWidth
                  size="small"
                  autoFocus
                  inputProps={{
                    maxLength: TagLimit,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="caption"
                            color={newTag.length == TagLimit ? 'error' : 'textSecondary'}
                            style={{ marginRight: '8px' }}>
                            {newTag.length}/{TagLimit}
                          </Typography>
                          <IconButton onClick={handleNewTagSubmit} aria-label="add">
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button variant="contained" color="primary" fullWidth onClick={handleAddTagOption} disabled={addingTag}>
                Add New Tag
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                disabled={!formValuesChanged || loading || formik.errors.title !== undefined || tagError}>
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
}

export default CustomerTagDrawer;
